/**
 * Generic Relationship Map DAG layout (frontend visualization only).
 * Topology + container geometry only — no domain-specific node names/IDs.
 */
import dagre from '@dagrejs/dagre';

function ensureStructuredClone() {
  const root = typeof window !== 'undefined' ? window : null;
  if (!root || typeof root.structuredClone === 'function') return;
  root.structuredClone = (value) => JSON.parse(JSON.stringify(value));
}
ensureStructuredClone();

export const RELATIONSHIP_NODE_MIN_WIDTH = 120;
export const RELATIONSHIP_NODE_MIN_HEIGHT = 50;
export const RELATIONSHIP_NODE_MAX_WIDTH = 180;

export const DEFAULT_LAYOUT_OPTIONS = {
  rankdir: 'LR',
  nodesep: 56,
  ranksep: 96,
  marginx: 20,
  marginy: 20,
  containerWidth: 920,
  containerHeight: 360,
  minReadableZoom: 0.7,
  fitPadding: 0.18,
};

export function estimateNodeDimensions(label) {
  const text = String(label || '').trim() || 'node';
  const chars = text.length;
  const width = Math.min(
    RELATIONSHIP_NODE_MAX_WIDTH,
    Math.max(RELATIONSHIP_NODE_MIN_WIDTH, Math.ceil(chars * 7.2) + 28),
  );
  const approxLines = Math.max(1, Math.ceil((chars * 7.2) / (width - 24)));
  const height = Math.max(RELATIONSHIP_NODE_MIN_HEIGHT, 28 + approxLines * 16);
  return { width, height };
}

function buildAdjacency(nodeIds, edges) {
  const outs = Object.fromEntries(nodeIds.map((id) => [id, []]));
  const ins = Object.fromEntries(nodeIds.map((id) => [id, []]));
  (edges || []).forEach((e) => {
    if (!e?.source || !e?.target) return;
    if (!outs[e.source] || !ins[e.target]) return;
    outs[e.source].push(e.target);
    ins[e.target].push(e.source);
  });
  return { outs, ins };
}

export function isMostlyLinearGraph(nodes, edges) {
  const ids = (nodes || []).map((n) => n.id);
  if (ids.length <= 1) return true;
  const { outs, ins } = buildAdjacency(ids, edges);
  // Only treat as linear when every node has at most one successor and one predecessor
  // (pure chains). Any fork or join needs LR/TB dagre with sibling lanes.
  return ids.every((id) => (outs[id] || []).length <= 1 && (ins[id] || []).length <= 1);
}

export function longestPathOrder(nodes, edges) {
  const ids = (nodes || []).map((n) => n.id);
  if (!ids.length) return [];
  const { outs, ins } = buildAdjacency(ids, edges);
  let best = [];
  const dfs = (id, path, visiting) => {
    if (visiting.has(id)) return;
    const next = [...path, id];
    if (next.length > best.length) best = next;
    visiting.add(id);
    (outs[id] || []).forEach((t) => dfs(t, next, visiting));
    visiting.delete(id);
  };
  const roots = ids.filter((id) => (ins[id] || []).length === 0);
  (roots.length ? roots : ids).forEach((id) => dfs(id, [], new Set()));
  if (best.length < 2) ids.forEach((id) => dfs(id, [], new Set()));
  const seen = new Set(best);
  ids.forEach((id) => {
    if (!seen.has(id)) best.push(id);
  });
  return best;
}

/**
 * Compute DAG ranks (longest-path level) from topology.
 * Rank increases strictly along directed edges when possible.
 */
export function computeDagRanks(nodes, edges) {
  const ids = (nodes || []).map((n) => String(n.id));
  const { outs, ins } = buildAdjacency(ids, edges);
  const rank = Object.fromEntries(ids.map((id) => [id, 0]));
  const indeg = Object.fromEntries(ids.map((id) => [id, (ins[id] || []).length]));
  const queue = ids.filter((id) => indeg[id] === 0);
  const order = [];
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    (outs[id] || []).forEach((t) => {
      rank[t] = Math.max(rank[t], rank[id] + 1);
      indeg[t] -= 1;
      if (indeg[t] === 0) queue.push(t);
    });
  }
  // Cycles / leftovers: keep rank 0+ existing
  ids.forEach((id) => {
    if (!order.includes(id) && rank[id] == null) rank[id] = 0;
  });
  return rank;
}

function runDagreLayout(sizedNodes, edges, options) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: options.rankdir || 'LR',
    nodesep: options.nodesep ?? DEFAULT_LAYOUT_OPTIONS.nodesep,
    ranksep: options.ranksep ?? DEFAULT_LAYOUT_OPTIONS.ranksep,
    marginx: options.marginx ?? DEFAULT_LAYOUT_OPTIONS.marginx,
    marginy: options.marginy ?? DEFAULT_LAYOUT_OPTIONS.marginy,
  });

  sizedNodes.forEach((n) => {
    g.setNode(n.id, { width: n.width, height: n.height });
  });
  (edges || []).forEach((e, i) => {
    if (!e?.source || !e?.target) return;
    if (!g.hasNode(e.source) || !g.hasNode(e.target)) return;
    g.setEdge(e.source, e.target, { id: e.id || `e-${i}` });
  });

  dagre.layout(g);

  const positions = {};
  const dagreRanks = {};
  sizedNodes.forEach((n) => {
    const dn = g.node(n.id);
    if (!dn) {
      positions[n.id] = { x: 0, y: 0 };
      dagreRanks[n.id] = 0;
      return;
    }
    positions[n.id] = {
      x: dn.x - n.width / 2,
      y: dn.y - n.height / 2,
    };
    dagreRanks[n.id] = typeof dn.rank === 'number' ? dn.rank : 0;
  });
  return { positions, dagreRanks };
}

function boundingBox(sizedNodes, positions) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  sizedNodes.forEach((n) => {
    const p = positions[n.id] || { x: 0, y: 0 };
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + n.width);
    maxY = Math.max(maxY, p.y + n.height);
  });
  if (!Number.isFinite(minX)) {
    return { width: 0, height: 0, minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
  return {
    width: maxX - minX,
    height: maxY - minY,
    minX,
    minY,
    maxX,
    maxY,
  };
}

function maxReadableGraphSize(options) {
  const width = Math.max(280, Number(options.containerWidth) || DEFAULT_LAYOUT_OPTIONS.containerWidth);
  const height = Math.max(180, Number(options.containerHeight) || DEFAULT_LAYOUT_OPTIONS.containerHeight);
  const pad = Number(options.fitPadding ?? DEFAULT_LAYOUT_OPTIONS.fitPadding);
  const zoom = Number(options.minReadableZoom ?? DEFAULT_LAYOUT_OPTIONS.minReadableZoom);
  const inner = Math.max(0.4, 1 - 2 * pad);
  return {
    maxWidth: (width * inner) / Math.max(0.35, zoom),
    maxHeight: (height * inner) / Math.max(0.35, zoom),
    containerWidth: width,
    containerHeight: height,
  };
}

/**
 * Fold consecutive DAG ranks into vertical columns.
 * Adjacent ranks stay adjacent (short edges).
 * alignMode:
 *  - 'staircase' (default): next column starts at previous last-rank Y (short horizontal hop)
 *  - 'pack': each column starts at Y=0 (denser fit; boundary edges may be longer)
 */
export function layoutRankColumns(sizedNodes, edges, options = {}) {
  const byId = Object.fromEntries(sizedNodes.map((n) => [n.id, n]));
  const ranksep = options.ranksep ?? DEFAULT_LAYOUT_OPTIONS.ranksep;
  const nodesep = options.nodesep ?? DEFAULT_LAYOUT_OPTIONS.nodesep;
  const alignMode = options.alignMode === 'pack' ? 'pack' : 'staircase';
  const limits = maxReadableGraphSize(options);
  const topoRanks = computeDagRanks(sizedNodes, edges);

  const rankToIds = {};
  sizedNodes.forEach((n) => {
    const r = topoRanks[n.id] || 0;
    rankToIds[r] = rankToIds[r] || [];
    rankToIds[r].push(n.id);
  });
  const orderedRanks = Object.keys(rankToIds)
    .map(Number)
    .sort((a, b) => a - b);

  // How many consecutive ranks fit in one vertical column at readable density.
  const avgH =
    sizedNodes.reduce((s, n) => s + n.height, 0) / Math.max(1, sizedNodes.length);
  const ranksPerColumn = Math.max(
    2,
    Math.floor((limits.maxHeight + nodesep) / (avgH + nodesep)),
  );

  const columns = [];
  for (let i = 0; i < orderedRanks.length; i += ranksPerColumn) {
    columns.push(orderedRanks.slice(i, i + ranksPerColumn));
  }

  const positions = {};
  const portHints = {}; // id -> { sourcePosition, targetPosition }
  let xCursor = 0;
  // Next column Y: staircase keeps a short horizontal hop; pack resets to 0.
  let nextColumnY = 0;

  columns.forEach((rankList, colIdx) => {
    let yCursor = alignMode === 'pack' ? 0 : nextColumnY;
    let colWidth = RELATIONSHIP_NODE_MIN_WIDTH;
    let lastRankBottom = yCursor;
    let lastRankTop = yCursor;

    rankList.forEach((rank, rankIdxInCol) => {
      const ids = rankToIds[rank] || [];
      // Sibling lanes within a rank: spread horizontally inside the column band.
      const totalW =
        ids.reduce((s, id) => s + byId[id].width, 0) + Math.max(0, ids.length - 1) * (nodesep * 0.6);
      colWidth = Math.max(colWidth, totalW);
      const startX = xCursor + Math.max(0, (colWidth - totalW) / 2);
      let x = startX;
      const rowH = Math.max(...ids.map((id) => byId[id].height), RELATIONSHIP_NODE_MIN_HEIGHT);
      ids.forEach((id) => {
        const n = byId[id];
        positions[id] = {
          x,
          y: yCursor + (rowH - n.height) / 2,
        };
        // Prefer vertical flow inside a column.
        portHints[id] = {
          sourcePosition: 'bottom',
          targetPosition: 'top',
        };
        x += n.width + nodesep * 0.6;
      });
      lastRankTop = yCursor;
      lastRankBottom = yCursor + rowH;
      yCursor += rowH + nodesep;
      // First rank in a non-first column: keep left target for lateral entry.
      if (colIdx > 0 && rankIdxInCol === 0) {
        ids.forEach((id) => {
          portHints[id] = { ...(portHints[id] || {}), targetPosition: 'left' };
        });
      }
      // Last rank in a column that has a successor column: exit right.
      if (rankIdxInCol === rankList.length - 1 && colIdx < columns.length - 1) {
        ids.forEach((id) => {
          portHints[id] = { ...(portHints[id] || {}), sourcePosition: 'right' };
        });
      }
    });

    nextColumnY = Math.max(0, lastRankTop);
    xCursor += colWidth + ranksep;
  });

  sizedNodes.forEach((n, i) => {
    if (!positions[n.id]) {
      positions[n.id] = { x: 0, y: i * (RELATIONSHIP_NODE_MIN_HEIGHT + nodesep) };
      portHints[n.id] = { sourcePosition: 'bottom', targetPosition: 'top' };
    }
  });

  return { positions, portHints, columns: columns.length, ranksPerColumn, alignMode };
}

/** @deprecated Keep export name for existing tests; delegates to rank-column fold. */
export function layoutWrappedPath(sizedNodes, edges, options = {}) {
  return layoutRankColumns(sizedNodes, edges, options).positions;
}

function nodesOverlap(a, b, pa, pb, gap = 8) {
  return !(
    pa.x + a.width + gap <= pb.x ||
    pb.x + b.width + gap <= pa.x ||
    pa.y + a.height + gap <= pb.y ||
    pb.y + b.height + gap <= pa.y
  );
}

export function countOverlappingPairs(sizedNodes, positions, gap = 8) {
  let count = 0;
  for (let i = 0; i < sizedNodes.length; i += 1) {
    for (let j = i + 1; j < sizedNodes.length; j += 1) {
      const a = sizedNodes[i];
      const b = sizedNodes[j];
      if (nodesOverlap(a, b, positions[a.id], positions[b.id], gap)) count += 1;
    }
  }
  return count;
}

/**
 * Approximate edge “detour” cost: length vs ideal straight distance between node centers.
 * Higher ratios mean long looping routes.
 */
export function averageEdgeDetourRatio(sizedNodes, positions, edges) {
  const byId = Object.fromEntries(sizedNodes.map((n) => [n.id, n]));
  const ratios = [];
  (edges || []).forEach((e) => {
    const a = byId[e.source];
    const b = byId[e.target];
    const pa = positions[e.source];
    const pb = positions[e.target];
    if (!a || !b || !pa || !pb) return;
    const acx = pa.x + a.width / 2;
    const acy = pa.y + a.height / 2;
    const bcx = pb.x + b.width / 2;
    const bcy = pb.y + b.height / 2;
    const straight = Math.hypot(bcx - acx, bcy - acy) || 1;
    // Orthogonal route length (manhattan) approximates smoothstep/step edges.
    const ortho = Math.abs(bcx - acx) + Math.abs(bcy - acy) || 1;
    ratios.push(ortho / straight);
  });
  if (!ratios.length) return 1;
  return ratios.reduce((s, r) => s + r, 0) / ratios.length;
}

/**
 * Choose source/target handle sides from relative geometry (generic).
 */
export function inferPortPositions(sourcePos, targetPos, sourceNode, targetNode, fallback = 'LR') {
  const scx = sourcePos.x + sourceNode.width / 2;
  const scy = sourcePos.y + sourceNode.height / 2;
  const tcx = targetPos.x + targetNode.width / 2;
  const tcy = targetPos.y + targetNode.height / 2;
  const dx = tcx - scx;
  const dy = tcy - scy;
  if (Math.abs(dy) >= Math.abs(dx)) {
    return {
      sourcePosition: dy >= 0 ? 'bottom' : 'top',
      targetPosition: dy >= 0 ? 'top' : 'bottom',
    };
  }
  return {
    sourcePosition: dx >= 0 ? 'right' : 'left',
    targetPosition: dx >= 0 ? 'left' : 'right',
  };
}

/**
 * Generic secondary label from metadata (never from domain name tables).
 */
export function genericNodeContextLabel(node) {
  if (!node || typeof node !== 'object') return '';
  const props = node.properties && typeof node.properties === 'object' ? node.properties : {};
  const candidates = [
    node.type,
    props.node_type,
    props.kind,
    props.role,
    props.source_api,
    props.api,
    props.api_id,
    props.operation,
    props.namespace,
    props.context,
    props.path_context,
    props.graph_role,
  ]
    .map((v) => String(v || '').trim())
    .filter((v) => v && v.toLowerCase() !== 'path' && v.toLowerCase() !== 'node');
  // Prefer a short distinct context token.
  const picked = candidates[0] || '';
  if (!picked) return '';
  if (String(node.label || '').toLowerCase() === picked.toLowerCase()) {
    return candidates[1] || '';
  }
  return picked;
}

export function layoutRelationshipGraph(nodes, edges, options = {}) {
  const opts = { ...DEFAULT_LAYOUT_OPTIONS, ...options };
  const list = Array.isArray(nodes) ? nodes.filter((n) => n && n.id != null) : [];
  if (!list.length) {
    return { positions: {}, sizedNodes: [], portHints: {}, meta: { strategy: 'empty' } };
  }

  const sizedNodes = list.map((n) => {
    const dims = estimateNodeDimensions(n.label || n.id);
    return {
      id: String(n.id),
      label: n.label || n.id,
      width: dims.width,
      height: dims.height,
      raw: n,
    };
  });

  const edgeList = Array.isArray(edges) ? edges : [];
  const limits = maxReadableGraphSize(opts);
  const linear = isMostlyLinearGraph(sizedNodes, edgeList);

  let strategy = 'dagre-lr';
  let positions = {};
  let portHints = {};
  let box;

  const applyDagre = (rankdir, override = {}) => {
    const result = runDagreLayout(sizedNodes, edgeList, { ...opts, ...override, rankdir });
    positions = result.positions;
    box = boundingBox(sizedNodes, positions);
    const defSrc = rankdir === 'TB' ? 'bottom' : 'right';
    const defTgt = rankdir === 'TB' ? 'top' : 'left';
    portHints = Object.fromEntries(
      sizedNodes.map((n) => [n.id, { sourcePosition: defSrc, targetPosition: defTgt }]),
    );
    // Refine per-edge geometry for cleaner local routing.
    edgeList.forEach((e) => {
      const a = sizedNodes.find((n) => n.id === e.source);
      const b = sizedNodes.find((n) => n.id === e.target);
      if (!a || !b || !positions[e.source] || !positions[e.target]) return;
      const ports = inferPortPositions(positions[e.source], positions[e.target], a, b, rankdir);
      portHints[e.source] = {
        ...portHints[e.source],
        sourcePosition: ports.sourcePosition,
      };
      portHints[e.target] = {
        ...portHints[e.target],
        targetPosition: ports.targetPosition,
      };
    });
  };

  if (linear && sizedNodes.length >= 3) {
    // Prefer top→bottom for sequential API/data flows (short local edges).
    // Keep a single column whenever possible — multi-column Y-reset creates long U-edges,
    // and staircase columns do not reduce height (same chain length).
    strategy = 'dagre-tb';
    applyDagre('TB', {
      nodesep: Math.min(opts.nodesep ?? 56, 36),
      ranksep: Math.min(opts.ranksep ?? 96, 52),
    });
    const zoomIfFit =
      limits.containerHeight > 0
        ? (limits.containerHeight * Math.max(0.4, 1 - 2 * (opts.fitPadding ?? 0.18))) /
          Math.max(1, box.height)
        : 1;
    // Only dense-pack when fit would become unreadable; prefer short edges otherwise.
    if (zoomIfFit < 0.38 && box.height > limits.maxHeight) {
      strategy = 'rank-columns';
      const folded = layoutRankColumns(sizedNodes, edgeList, {
        ...opts,
        nodesep: Math.min(opts.nodesep ?? 56, 40),
        ranksep: Math.min(opts.ranksep ?? 96, 80),
        alignMode: 'pack',
      });
      positions = folded.positions;
      portHints = folded.portHints;
      box = boundingBox(sizedNodes, positions);
    }
  } else {
    applyDagre('LR');
    if (box.width > limits.maxWidth) {
      strategy = 'dagre-tb';
      applyDagre('TB');
      if (box.height > limits.maxHeight * 1.6) {
        strategy = 'rank-columns';
        const folded = layoutRankColumns(sizedNodes, edgeList, opts);
        positions = folded.positions;
        portHints = folded.portHints;
        box = boundingBox(sizedNodes, positions);
      }
    }
  }

  return {
    positions,
    sizedNodes,
    portHints,
    meta: {
      strategy,
      bbox: box,
      limits,
      nodeCount: sizedNodes.length,
      edgeCount: edgeList.length,
      avgEdgeDetour: averageEdgeDetourRatio(sizedNodes, positions, edgeList),
      linear,
    },
  };
}

export default layoutRelationshipGraph;
